import React, { Fragment } from 'react';

import styled from 'styled-components';
import AddressElement from '../address-element';
import ChooserElement from '../chooser-element';
import InputElement from '../input-element';
import MultiChooseElement from '../multi-choose-element';
import MultichoiceElement from '../multichoice-element';
import PhoneElement from '../phone-element';
import SignatureElement from '../signature-element';
import RadioElement from '../radio-element';
import StarRatingElement from '../star-rating-element';
import TextDisplayElement from '../text-display-element';

// Component types that are display-only (no lexicon required, no user input)
const DISPLAY_TYPES = ['TEXT', 'HEADER', 'DISPLAY'];

const WidgetComponent = ({ component, onChange, value, attributes, disabled = false, hasError = false }) => {
  const componentMap = {
    NUMERIC: { component: InputElement, props: { type: 'number', variant: 'size_44' } },
    ALPHA: { component: InputElement, props: { variant: 'size_44' } },
    ALPHANUMERIC: { component: InputElement, props: { variant: 'size_44' } },
    TEXTAREA: { component: InputElement, props: { type: 'textarea', rows: 10, variant: 'size_44' } },
    ADDRESS: { component: AddressElement, props: { variant: 'size_44' } },
    PHONE: { component: PhoneElement, props: { type: 'textarea', variant: 'size_44', countryCode: false } },
    CHOOSER: { component: ChooserElement, props: { variant: 'size_44' } },
    RADIO: { component: RadioElement },
    CHOOSERMULTI: { component: MultiChooseElement },
    MULTICHOICE: { component: MultichoiceElement },
    SIGNATURE: { component: SignatureElement },
    RANGE: { component: StarRatingElement },
    TEXT: { component: TextDisplayElement },
    HEADER: { component: TextDisplayElement },
    DISPLAY: { component: TextDisplayElement },
    DEFAULT: { component: () => <Fragment /> },
  };

  // Resolve display mode:
  // - Explicit display types (TEXT, HEADER, DISPLAY)
  // - Any component with component_text field = static display (lexicon may be a dummy value)
  // - TEXTAREA/ALPHA with content and no lexicon = static display
  const isDisplayComponent =
    DISPLAY_TYPES.includes(component.component_type) ||
    DISPLAY_TYPES.includes(component.component_layout) ||
    !!component.component_text ||
    (component.content && !component.lexicon);

  // If the component has is_multiline: true, treat ALPHA/ALPHANUMERIC as TEXTAREA
  const resolvedLayout = isDisplayComponent
    ? 'TEXT'
    : component.is_multiline && ['ALPHA', 'ALPHANUMERIC'].includes(component.component_type)
    ? 'TEXTAREA'
    : component.component_layout;

  const Component =
    componentMap[resolvedLayout] || componentMap[component.component_type] || componentMap['DEFAULT'];

  // For CHOOSER components, don't use name as placeholder
  // For input fields (ALPHA, NUMERIC, ALPHANUMERIC), don't use name/label as placeholder - they're shown as labels above
  const getPlaceholder = () => {
    if (['CHOOSER', 'RADIO'].includes(component.component_type) || ['CHOOSER', 'RADIO'].includes(component.component_layout)) {
      return component.sub_label || component.label || '';
    }
    // For input fields, only use sub_label if provided, otherwise leave empty (name/label shown above field)
    if (
      component.component_type === 'ALPHA' ||
      component.component_type === 'NUMERIC' ||
      component.component_type === 'ALPHANUMERIC' ||
      component.component_layout === 'ALPHA' ||
      component.component_layout === 'NUMERIC' ||
      component.component_layout === 'ALPHANUMERIC'
    ) {
      return component.sub_label || '';
    }
    return component.sub_label || component.label || component.name;
  };

  return (
    <WidgetComponentWrapper hasError={hasError && !isDisplayComponent}>
      <Component.component
        {...component}
        placeholder={getPlaceholder()}
        onChange={onChange}
        value={value}
        attributes={attributes}
        font={component.font || attributes?.font || null}
        disabled={disabled}
        {...Component.props}
      />
    </WidgetComponentWrapper>
  );
};

const WidgetComponentWrapper = styled('div')`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  ${({ hasError }) =>
    hasError &&
    `
    border: 2px solid #FF5B5B;
    border-radius: 8px;
    padding: 4px;
    animation: shake 0.3s ease;
  `}

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-4px); }
    75% { transform: translateX(4px); }
  }
`;

export default WidgetComponent;
